import { Theme, css, useTheme } from '@emotion/react';

import { ReactNode, useMemo } from 'react';

import { FontSizes, Radius, Spacing } from 'chatie-design-system';

import { CSSProps, OnClickProps } from '@utils/misc';

const badgeCSS = (radius: number, fontSize: number) => css`
  font-size: ${fontSize}px;
  display: inline-block;
  height: 18px;
  line-height: 16px;
  border-radius: ${radius}px;
  font-weight: 500;
`;

const badgeColorCSS =
  (
    color: string,
    backgroundColor: string,
    border = false,
    isGradient = false,
    isClickable = false
  ) =>
  (theme: Theme) =>
    css`
      color: ${color};
      ${isClickable &&
      css`
        cursor: pointer;
      `}

      ${border
        ? css`
            ${isGradient
              ? css`
                  border: 1px solid transparent;
                  background-image: linear-gradient(
                      ${theme.var.white},
                      ${theme.var.white}
                    ),
                    ${backgroundColor};
                  background-origin: border-box;
                  background-clip: content-box, border-box;
                `
              : css`
                  border: 1px solid ${backgroundColor};
                  background-color: ${theme.var.white};
                `}

            span {
              padding: 0 ${Spacing.xxhsmall}px;
            }
          `
        : css`
            ${isGradient
              ? css`
                  background-image: ${backgroundColor};
                `
              : css`
                  background-color: ${backgroundColor};
                `}

            span {
              padding: 0 ${Spacing.xxsmall}px;
            }
          `}
    `;

export type BadgeProps = {
  text: string;
  color?: string;
  backgroundColor?: string;
  border?: boolean;
  radius?: number;
  fontSize?: number;
};

const Badge = ({
  className,
  text,
  color,
  border,
  backgroundColor,
  onClick,
  radius = Radius.xsmall,
  fontSize = FontSizes.xxsmall,
}: BadgeProps & CSSProps & OnClickProps) => {
  const theme = useTheme();
  const isGradient = backgroundColor?.includes('linear-gradient') ?? false;

  return (
    <span
      css={[
        badgeCSS(radius, fontSize),
        badgeColorCSS(
          color ?? theme.true.white,
          backgroundColor ?? theme.var.gray500,
          border,
          isGradient,
          !!onClick
        ),
      ]}
      className={className}
      onClick={onClick}
    >
      <span>{text}</span>
    </span>
  );
};

export default Badge;

/** List Component */
const badgeListCSS = css`
  display: inline-block;
  font-size: 0;

  * + * {
    margin-left: ${Spacing.xxsmall}px;
  }
`;

type BadgeListProps = {
  children?: ReactNode;
};

export const BadgeList = ({ children }: BadgeListProps) => {
  return <div css={badgeListCSS}>{children}</div>;
};

export const useBadgePresets = () => {
  const theme = useTheme();

  const presets = useMemo(
    () =>
      ({
        // fill
        grayFill: {
          backgroundColor: theme.var.gray400,
          color: theme.true.white,
        },
        mainFill: {
          backgroundColor: theme.colors.main500,
          color: theme.true.white,
        },
        blueFill: {
          backgroundColor: theme.colors.blue500,
          color: theme.true.white,
        },
        pinkFill: {
          backgroundColor: theme.colors.pink500,
          color: theme.true.white,
        },
        redFill: {
          backgroundColor: theme.colors.red500,
          color: theme.true.white,
        },
        purpleFill: {
          backgroundColor: theme.service.violet,
          color: theme.true.white,
        },
        honorFill: {
          backgroundColor: 'linear-gradient(to right, #17dce4, #4db4ee)',
          color: theme.true.white,
        },
        new: {
          backgroundColor: theme.colors.red500,
          color: theme.true.white,
        },
        // border
        pink: {
          backgroundColor: theme.colors.pink500,
          border: true,
          color: theme.colors.pink500,
        },
        yellow: {
          backgroundColor: theme.colors.yellow500,
          border: true,
          color: theme.colors.yellow500,
        },
        orange: {
          backgroundColor: theme.colors.yellow700,
          border: true,
          color: theme.colors.yellow700,
        },
        red: {
          backgroundColor: theme.colors.red500,
          border: true,
          color: theme.colors.red500,
        },
        blue: {
          backgroundColor: theme.colors.blue500,
          border: true,
          color: theme.colors.blue500,
        },
        main: {
          backgroundColor: theme.colors.main500,
          border: true,
          color: theme.colors.main500,
        },
        fan: {
          backgroundColor: theme.colors.blue300,
          border: true,
          color: theme.colors.blue300,
        },
        famousFill: {
          backgroundColor: theme.colors.playGra,
          color: theme.true.white,
        },
      } as {
        [key: string]: Pick<BadgeProps, 'color' | 'backgroundColor' | 'border'>;
      }),
    []
  );

  return presets;
};
